import {
  BodyLinkV3,
  LayoutGrid,
  TypographyRhythm,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';

import PageLayoutV3 from '@/app/components/PageLayoutV3';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';

import { AccessAndUseSection } from '../_components/AccessAndUseSection';
import { TermsOfDeliverySection } from '../_components/TermsOfDeliverySection';

const Page = (): JSX.Element => {
  const enable2024Terms = useFeatureFlag('enable-2024-terms-of-use');
  const t = useTranslations();

  return (
    <PageLayoutV3
      pageName="Terms and Conditions"
      containerProps={{
        name: 'terms-and-conditions',
        padding: 'both',
        lang: 'it',
      }}
      footerProps={{
        wrapperClassName: 'bg-white',
      }}
    >
      <LayoutGrid data-cy="terms-and-conditions-it-page">
        <TypographyRhythm className="col-main">
          <>
            <TypographyV3 Element="h1" variant="h1">
              CONDIZIONI D'USO DI ŌURA
            </TypographyV3>
            <TypographyV3>
              <em>Ultimo aggiornamento: 2 aprile 2024</em>
            </TypographyV3>
            <TypographyV3>
              Ti diamo il benvenuto e ti ringraziamo per l'interesse dimostrato
              per Ōura! Le presenti Condizioni d'uso (l'"Accordo") descrivono i
              termini e le condizioni applicabili all'utilizzo di ouraring.com
              (il "Sito"), l'anello intelligente, il caricabatterie e/o altri
              dispositivi elettronici (i "Prodotti"), inclusa l'applicazione
              mobile Ōura (l'"App Mobile") (collettivamente, i "Servizi"). Il
              Sito e l'App Mobile sono di proprietà e gestiti da{' '}
              <strong>Ōura Health Oy</strong>, nonché dalle sue affiliate e
              controllate, come Ouraring Inc. (collettivamente “Ōura” o “Oura”).
              “Oura”).
            </TypographyV3>
            <TypographyV3>
              Nel presente Accordo, ci riferiamo a noi stessi come "Ōura" o
              "Oura" o "ci" o "noi"; ci riferiamo a te come "Utente" o
              "Cliente". Oura e il Cliente vengono indicati, nel presente
              Accordo, individualmente come "Parte" e collettivamente come
              "Parti".
            </TypographyV3>
            <TypographyV3>
              Accedendo o utilizzando i Servizi, compreso l'accesso al Sito,
              l'Utente si propone e accetta espressamente di essere vincolato da
              tutti i termini e le condizioni del presente Accordo e dalla
              Politica sulla privacy (disponibile su{' '}
              <BodyLinkV3 color="inherit" href="/privacy-policy">
                ouraring.com/privacy-policy
              </BodyLinkV3>
              ), incorporata per riferimento. Se l'Utente non accetta i termini
              e le condizioni qui presenti, non può utilizzare i Servizi.
            </TypographyV3>

            <AccessAndUseSection />

            <TypographyV3 Element="h2" variant="h3">
              Responsabilità del Cliente
            </TypographyV3>
            <TypographyV3>
              L'Utente riconosce di essere l'unico responsabile per l'uso dei
              Servizi, direttamente o indirettamente; spetta, inoltre, allo
              stesso comprendere se tale accesso o uso è consentito o viola il
              presente Accordo. L'Utente è inoltre l'unico responsabile per il
              rispetto di tutte le leggi applicabili relative all'utilizzo dei
              Servizi. Inoltre, l'Utente utilizzerà i Servizi esclusivamente per
              scopi legali e condurrà tutte le attività attraverso i Servizi in
              conformità a tutte le leggi e i regolamenti applicabili, comprese,
              ma non solo, tutte le leggi e i regolamenti federali e statali
              applicabili che disciplinano l'offerta e la vendita di titoli, il
              riciclaggio di denaro e l'antiterrorismo.
            </TypographyV3>
            <TypographyV3>
              L'Utente è l'unico responsabile di assicurarsi sempre e comunque
              di essere in grado di accedere e utilizzare i Servizi, anche
              garantendo la compatibilità di hardware, App Mobile, accesso a
              internet, sicurezza dell'App Mobile, dispositivi o servizi di
              backup di proprietà, e qualsiasi altro requisito. Oura non ha
              alcuna responsabilità di fornire qualsivoglia App Mobile o
              hardware aggiuntivo. L'Utente accetta inoltre che Oura non ha
              alcuna responsabilità per qualsiasi perdita di dati o altri danni
              o perdite subite in relazione all'uso dei Servizi, compresa la
              mancata fornitura di adeguati dispositivi di sicurezza o di backup
              o servizi.
            </TypographyV3>
            <TypographyV3>
              L'Utente è responsabile di garantire che Oura disponga di
              informazioni accurate e aggiornate per il proprio account Cliente,
              comprese le informazioni di contatto e di pagamento attualizzati.
              L'Utente è inoltre responsabile per il controllo regolare
              dell'account e-mail associato al Cliente allo scopo di ricevere
              qualsivoglia comunicazione da parte di Oura.
            </TypographyV3>
            <TypographyV3>
              Se viene fornito un nome utente, una password, un file di
              credenziali o qualsiasi altra informazione, nell'ambito di una
              qualsivoglia procedura di sicurezza ("Credenziali"), l'Utente
              dovrà trattare tali informazioni come riservate e non divulgare le
              Credenziali ad altre persone o ad altri enti. L'Utente riconosce
              che account e Credenziali sono personali e accetta di non fornire
              a nessun'altra persona l'accesso ai Servizi o a parti dei Servizi
              utilizzando il suo nome utente, la sua password o altre
              informazioni di sicurezza. L'Utente dovrà notificare
              immediatamente Oura circa qualsiasi accesso o uso non autorizzato
              delle sue credenziali o in merito a qualsivoglia altra violazione
              della sicurezza. In qualunque momento, Oura avrà il diritto di
              disattivare qualsiasi nome utente, password, file di credenziali o
              altro identificatore, sia esso scelto dall'utente o fornito da
              Oura.
            </TypographyV3>
            <TypographyV3>
              Oura effettuerà sforzi commercialmente ragionevoli per fornire
              servizi di supporto adeguati ai Servizi. Fatto salvo quanto sopra,
              il presente Accordo non dà diritto ad alcuna garanzia di livello,
              disponibilità o tempo di risposta dei servizi di supporto per i
              Servizi.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Pagamento e tariffe
            </TypographyV3>
            <TypographyV3>
              I Servizi a pagamento includono i Servizi che possono essere
              acquisti una tantum o servizi in abbonamento a rinnovo automatico
              ("Servizi a pagamento"), compresi il nostro Sito e l'App Mobile
              ("Abbonamenti"). Possiamo apportare modifiche, sospendere o
              interrompere i Servizi a pagamento in qualsiasi momento e per
              qualsiasi motivo e Oura si riserva l'esclusiva discrezione di
              determinare quali Servizi o parti di essi richiedano il pagamento.
            </TypographyV3>
            <TypographyV3>
              I Servizi a pagamento possono includere Prodotti pre-ordinati che
              saranno prodotti per l'Utente in futuro ("Pre-Ordine"). Al momento
              del pre-ordine, verrà addebitata una tariffa di pre-ordine. La
              data effettiva di spedizione di qualsiasi pre-ordine accettato
              dipenderà da una serie di fattori, tra cui, ma non solo, la data
              di pagamento della tariffa di pre-ordine e il programma di
              produzione di Oura. Non vi è una data di spedizione garantita per
              i pre-ordini.
            </TypographyV3>
            <TypographyV3>
              L'Utente accetta di pagare tutte le tariffe applicabili per i
              Servizi a pagamento, comprese, senza esclusione, qualsiasi tariffa
              di abbonamento mensile, tariffe utente e tariffe di offerta nonché
              qualsiasi altra tariffa, onere o costo che l'Utente accetta di
              sostenere come parte dei Servizi a pagamento durante il processo
              di check-out ("Tariffe"). L'Utente accetta di pagare tutte le
              tariffe e tutte le tasse applicabili sostenute prima dello
              scioglimento o della cancellazione dell'Accordo.
            </TypographyV3>
            <TypographyV3>
              L'Utente autorizza Oura ad addebitare, attraverso il metodo di
              pagamento impostato, gli eventuali Servizi a pagamento. Fornendo
              un metodo di pagamento accettabile, l'Utente dichiara e garantisce
              di disporre dell'autorizzazione per utilizzare tale metodo di
              pagamento e di autorizzare noi o il nostro sistema di elaborazione
              dei pagamenti di terze parti ad addebitare, tramite il metodo di
              pagamento sopraccitato, l'importo totale dell'acquisto, comprese
              eventuali tasse e altre spese applicabili. Se il metodo di
              pagamento non può essere verificato, non è valido o non è
              altrimenti accettabile, il Servizio a pagamento può essere sospeso
              o annullato. L'Utente è tenuto a risolvere qualsiasi problema che
              dovessimo riscontrare in relazione al metodo di pagamento fornito
              per poter procedere all'utilizzo del Servizio. In caso di
              accettazione di un'offerta promozionale o di modifiche ai Servizi
              a pagamento, le tariffe, le tasse e gli importi fatturati possono
              variare. Gli importi di fatturazione possono variare anche a causa
              delle variazioni delle tasse o dei tassi di cambio della valuta
              applicabili. L'Utente autorizza noi o il nostro sistema di
              elaborazione dei pagamenti di terze parti ad addebitare sul metodo
              di pagamento impostato l'importo corrispondente. I rimborsi
              saranno emessi solo ed esclusivamente se obbligatori per legge.
              Eventuali sconti individuali non sono cumulabili con altri sconti
              di qualunque tipo. Il presente obbligo di pagamento sopravvive
              allo scioglimento o alla cancellazione del presente Accordo, per
              qualsiasi motivo.
            </TypographyV3>
            <TypographyV3>
              Se l'Utente sceglie di finanziare un acquisto attraverso il nostro
              processore di pagamento di terze parti e sono presenti nel suo
              ordine uno o più articoli con una data di spedizione estesa, il
              pagamento del prestito, compresi gli interessi, può essere dovuto
              prima di spedire tutti gli articoli. Facciamo presente che non si
              può ricevere un rimborso di eventuali interessi già maturati su un
              importo rimborsato in un secondo momento.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Abbonamenti{' '}
            </TypographyV3>
            <TypographyV3>
              Alcuni Servizi a pagamento sono acquisti sotto forma di
              abbonamento, ai quali si applicano i seguenti termini:
            </TypographyV3>
            <TypographyV3>
              <strong>
                La durata dell'Abbonamento varia e può corrispondere a una
                durata “continua”, “mensile” o “annuale” ("Periodo/i di
                Abbonamento"), così come descritto nel corso dell'acquisto dei
                Servizi a pagamento. L'Abbonamento si rinnoverà automaticamente
                per ulteriori Periodi di Abbonamento fino a quando l'Abbonamento
                non sarà cancellato dall'Utente oppure sospeso o terminato da
                Oura. Se non diversamente indicato da noi, prima o all'inizio di
                ciascun Periodo di Abbonamento il metodo di pagamento designato
                sarà utilizzato per eventuali addebiti della tariffa di
                Abbonamento più eventuali tasse e altri oneri applicabili. Prima
                di addebitare un Periodo di Abbonamento, all'Utente verranno
                comunicate le tariffe applicabili e il rinnovo avverrà al prezzo
                in vigore al momento dell'acquisto del Servizio a pagamento.
              </strong>
            </TypographyV3>
            <TypographyV3>
              <strong>
                L'Abbonamento può essere cancellato in qualsiasi momento. La sua
                cancellazione avrà effetto alla fine del Periodo di Abbonamento
                in corso. Per cancellare l'Abbonamento e il pagamento
                automatico, fare clic sul tasto "Elimina account" dalla
                schermata dell'account personale o inviare un'e-mail a
                support@ouraring.com. La cancellazione non dà diritto al
                rimborso delle Tariffe pagate in precedenza e non si riceverà un
                rimborso proporzionale per il resto del Periodo di Abbonamento.
                Nel caso in cui l'Utente cancelli l'Abbonamento, si noti che
                potremmo ancora inviargli comunicazioni promozionali, a meno che
                non sia l'Utente stesso a scegliere di non ricevere tali
                comunicazioni seguendo le istruzioni di annullamento
                dell'iscrizione fornite nelle comunicazioni.
              </strong>
            </TypographyV3>
            <TypographyV3>
              <strong>
                Quando si cancella un Abbonamento, si annullano solo gli
                addebiti futuri per l'Abbonamento stesso. L'Utente non riceverà
                un rimborso del Periodo di Abbonamento corrente per il quale ha
                proceduto al pagamento, ma l'Utente continuerà ad avere pieno
                accesso a suddetto Abbonamento fino alla fine del Periodo di
                Abbonamento corrente.
              </strong>{' '}
              In qualsiasi momento, per qualsiasi motivo, possiamo fornire un
              rimborso, uno sconto o un corrispettivo altro ("crediti") ad
              alcuni o a tutti i nostri utenti. L'importo e la forma di tali
              crediti, nonché la decisione di fornirli, sono a nostra sola e
              assoluta discrezione. L'erogazione di crediti in un caso non dà
              diritto a crediti successivi in casi simili, né ci obbliga a
              fornire crediti in futuro.
            </TypographyV3>
            <TypographyV3>
              Se risiede al di fuori degli Stati Uniti e cambia idea circa un
              acquisto, l'Utente può avere diritto a ricevere un rimborso
              completo entro quattordici (14) giorni (il "Periodo di
              riflessione"), a condizione che non abbia effettuato l'accesso o
              altrimenti riscattato o iniziato a utilizzare i Servizi come
              abbonato durante il Periodo di riflessione.
            </TypographyV3>
            <TypographyV3>
              Di tanto in tanto, per alcuni Abbonamenti possiamo offrire prove
              gratuite per determinati periodi di tempo.{' '}
              <strong>
                Prima di iniziare la prova gratuita comunicheremo le tariffe di
                Abbonamento applicabili eventualmente addebitate alla scadenza
                della prova gratuita. A meno che l'Abbonamento non venga
                annullato prima della fine della prova gratuita, seguendo i
                passaggi descritti sopra, quando la prova gratuita termina, noi
                o il nostro processore di pagamento di terze parti fattureremo
                attraverso il metodo di pagamento designato su base ricorrente
                il costo dell'Abbonamento, più eventuali tasse e altri costi
                applicabili, per tutto il tempo in cui l'Abbonamento continua.
                Per evitare qualsiasi addebito, è necessario cancellare
                l'Abbonamento prima della fine del periodo di prova gratuito. Le
                istruzioni per cancellare l'Abbonamento sono descritte sopra.{' '}
              </strong>
            </TypographyV3>
            <TypographyV3>
              I dati di pagamento dell'Utente saranno elaborati e conservati
              attraverso un processore di pagamento di terze parti. Tutti i
              titolari di un account a pagamento devono mantenere attivo almeno
              un metodo di pagamento valido per il pagamento delle Tariffe
              descritte più dettagliatamente durante il check-out. Tutte le
              Tariffe sono calcolate e fatturate su base mensile o annuale a
              seconda della scelta effettuata e sono dovute immediatamente dopo
              la ricezione e sono soggette a modifiche. L'Utente riconosce che
              le Tariffe hanno una funzionalità di pagamento ricorrente e
              accetta la responsabilità di tutte le spese ricorrenti prima della
              cancellazione. Le Tariffe saranno addebitate attraverso il metodo
              di pagamento salvato e designato fornito dall'utente un giorno
              prima dell'anniversario mensile o annuale della data di acquisto
              iniziale.
            </TypographyV3>
            <TypographyV3>
              Nel caso in cui l'Utente non abbia effettuato l'accesso o
              altrimenti utilizzato i Servizi per sei (6) mesi o più dopo
              l'acquisto o la ricezione di un Prodotto durante una prova
              gratuita, ci riserviamo il diritto di terminare l'Abbonamento,
              annullare qualsiasi acquisto in sospeso e rimborsare solo il
              prezzo di acquisto del Prodotto. Non si avrà diritto a un rimborso
              per il valore dell'Abbonamento durante la prova gratuita.
            </TypographyV3>
            <TypographyV3>
              In qualunque momento, Oura si riserva il diritto di modificare le
              Tariffe per i nostri Servizi a pagamento, o qualsiasi funzionalità
              o parte dei nostri Servizi a pagamento. L'Utente riconosce che
              Oura può modificare le Tariffe per i Servizi a pagamento in
              qualsiasi momento. In caso di modifica, Oura ne darà notifica
              tramite l'indirizzo e-mail associato all'account personale almeno
              trenta (30) giorni prima della data effettiva della modifica in
              questione. La continuità nell'uso dei Servizi corrisponderà
              all'accettazione di qualsiasi modifica delle Tariffe. L'Utente è
              l'unico responsabile per tutte le tasse applicabili e verranno
              addebitate tasse quando richiesto dalla legge.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Dati e comunicazioni
            </TypographyV3>
            <TypographyV3>
              Oura può raccogliere ed elaborare informazioni relative
              all'utilizzo dei Servizi da parte dell'Utente. L'Utente acconsente
              alla raccolta e all'utilizzo di tali informazioni da parte di
              Oura, così come alla condivisione di tali informazioni con
              fornitori di servizi terzi allo scopo di erogare, commercializzare
              e migliorare i Servizi, e per qualsivoglia altra ragione descritta
              nella Politica sulla privacy. Tutte le informazioni personali
              raccolte da Oura sono trattate in conformità alla
              <BodyLinkV3 color="inherit" href="/privacy-policy">
                Politica sulla privacy
              </BodyLinkV3>
              .
            </TypographyV3>
            <TypographyV3>
              <strong>
                Accettando i termini e le condizioni del presente Accordo, e
                fornendo le informazioni di contatto personali a Oura, l'Utente
                dà esplicito consenso a Oura, ai suoi affiliati e agenti, di
                contattarlo di tanto in tanto, a qualsiasi indirizzo postale,
                numero di telefono o indirizzo e-mail eventualmente fornito a
                Oura.
              </strong>{' '}
              Per consenso si intende che l'Utente accetta di essere contattato
              da Oura e dai suoi fornitori di servizi per telefono, e-mail, SMS
              o altro per qualsiasi scopo, inclusi, ma non in via limitativa,
              notifiche relative a Servizi e account personale, abbonamenti,
              acquisti, aggiornamenti disponibili, problemi di fatturazione ed
              elaborazione dei pagamenti e comunicazioni di telemarketing. Tali
              comunicazioni autorizzate possono includere l'uso di tecnologie di
              composizione automatica o l'uso di messaggi preregistrati. Quando
              lo contattiamo, l'Utente è responsabile di tutte le spese
              eventualmente addebitategli dal suo fornitore di servizi.
              Riconosce altresì che il consenso a quanto sopra non è una
              condizione per l'utilizzo dei Servizi Oura e che, qualora non
              desiderasse acconsentire, può contattarci e richiedere di essere
              inserito in un elenco da non contattare; l'Utente può inoltre
              abbandonare in qualsiasi momento questa opzione utilizzando il
              meccanismo di opt-out presente in tutte le comunicazioni.
            </TypographyV3>
            <TypographyV3>
              Ai sensi del presente Accordo, Oura declina ogni responsabilità
              per qualsiasi informazione fornita a Oura e che possa costituire
              una cartella clinica elettronica, o informazioni simili,
              eventualmente fornita dall'Utente o da un utente finale, fatta
              salva qualsiasi cosa in contrario di cui al presente Accordo o
              come altrimenti richiesto da qualsivoglia legge, norma o
              regolamento federale, statale o internazionale applicabile.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Diritti di proprietà intellettuale
            </TypographyV3>
            <TypographyV3>
              ŌURA, Ō e OURA sono marchi di Ōura Health Oy. Il contenuto di
              Oura, i prodotti di Oura, le funzionalità e i Servizi di Oura e la
              nostra tecnologia alla base sono tutelati da copyright, marchi,
              brevetti, proprietà intellettuale e altre leggi degli Stati Uniti
              e di Paesi stranieri. Tutti i diritti riservati. Non viene
              concessa, per implicazione o altrimenti, alcuna licenza o diritto
              di utilizzare qualsiasi marchio che appaia o che sia utilizzato o
              visualizzato in relazione ai Servizi ("Marchi"). I Servizi possono
              anche contenere o fare riferimento a marchi, nomi commerciali,
              nomi di prodotti e loghi di terzi che possono essere marchi
              registrati dei rispettivi proprietari. In nessun caso è possibile
              utilizzare o copiare anche solo uno dei marchi. Nulla qui deve
              essere interpretato come la concessione di qualsiasi licenza o
              diritto di utilizzare qualsiasi Marchio visualizzato in
              connessione con i Servizi senza l'espressa autorizzazione scritta
              di Oura.
            </TypographyV3>
            <TypographyV3>
              Tutti i contenuti forniti in associazione ai Servizi e al presente
              Contratto, inclusi, ma non limitati a, il Sito, il Prodotto, l'App
              Mobile, tutti i testi, la grafica, le interfacce utente, le
              interfacce visive, le fotografie, le immagini/video, l'arte
              elettronica, i suoni/audio, i dati, i programmi di comunicazione,
              il codice eseguibile, il codice informatico e i dati
              (collettivamente, "Contenuto") formattati, organizzati e raccolti
              in una varietà di forme, compreso il design, la struttura, la
              selezione, il coordinamento, l'espressione, il "look and feel", la
              disposizione, i layout, le pagine, le schermate e i database di
              tale Contenuto, contenuti nel Contenuto, nei Servizi e nella
              tecnologia sottostante, e qualsiasi altro lavoro protetto da
              copyright associato ai Servizi ("Opere protette da copyright"),
              sono di proprietà esclusiva, controllate o concesse in licenza da
              o a Oura e sono protette dalle leggi sul copyright degli Stati
              Uniti e internazionali. L'Utente accetta di non copiare,
              riprodurre, modificare, creare opere derivate, distribuire o
              mostrare pubblicamente le opere protette da copyright direttamente
              o indirettamente senza il previo consenso scritto di Oura.
            </TypographyV3>
            <TypographyV3>
              Se l'Utente fornisce comunicazioni o materiali a Oura per posta,
              e-mail, telefono o altro, suggerendo o raccomandando modifiche ai
              Servizi, comprese, senza limitazione, nuove caratteristiche o
              funzionalità relative, o qualsiasi commento, domanda, suggerimento
              o simili ("Feedback"), Oura è libera di utilizzare tale Feedback
              indipendentemente da qualsiasi altro obbligo o limitazione tra le
              Parti che disciplina tale Feedback. Oura è libera di utilizzare,
              senza alcuna attribuzione o compenso a qualsiasi parte, qualsiasi
              idea, know-how, concetto, tecnica o altri diritti di proprietà
              intellettuale contenuti nel Feedback, per qualsiasi scopo, anche
              se Oura non è tenuta a utilizzare nessun Feedback.
            </TypographyV3>
            <TypographyV3>
              Oura rispetta i diritti di proprietà intellettuale degli altri ed
              è nostra politica elaborare ed esaminare rapidamente le notifiche
              di presunta violazione del copyright o di altre leggi applicabili
              in materia di proprietà intellettuale. Qualsiasi notifica di
              presunta violazione deve essere inviata all'agente designato da
              Oura all'indirizzo ip@ouraring.com e deve contenere tutte le
              seguenti informazioni: (i) una firma (fisica o elettronica) del
              proprietario del copyright o di una persona autorizzata ad agire
              per conto del proprietario del copyright; (ii) una descrizione
              dell'opera tutelata da copyright che si consideri violata; (iii)
              una descrizione del materiale che si ritiene stia violando e che
              deve essere rimosso o il cui accesso deve essere disabilitato, e
              informazioni sufficienti a permettere agli amministratori di Oura
              di individuare il materiale; (iv) informazioni sufficienti a
              permetterci di contattare l'Utente, come indirizzo, numero di
              telefono e indirizzo e-mail; (v) una dichiarazione ritenuta
              dall'Utente in buona fede che l'uso del materiale nella maniera
              lamentata non sia autorizzato dal proprietario del copyright, dal
              suo agente o dalla legge; e (vi) una dichiarazione che le
              informazioni nella notifica sono accurate e, sotto pena di
              spergiuro, che l'Utente è il proprietario del copyright o che è
              autorizzato ad agire per conto del proprietario del copyright
              presumibilmente violato.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Limitazioni di garanzia
            </TypographyV3>
            <TypographyV3>
              I SERVIZI VENGONO FORNITI “COME SONO CON TUTTI I DIFETTI” E “COME
              DISPONIBILI” SENZA GARANZIE DI ALCUN TIPO, E CON LA PRESENTE OURA
              E LE SUE CONTROLLATE E AFFILIATE E I SUOI FUNZIONARI, DIRETTORI,
              DIPENDENTI, RAPPRESENTANTI, AGENTI, PARTNER E LICENZIATARI
              ESCLUDONO TUTTE LE GARANZIE E LE CONDIZIONI RELATIVE AI SERVIZI,
              SIA ESPLICITE SIA IMPLICITE, ED ESCLUDONO ESPRESSAMENTE QUALSIASI
              GARANZIA IMPLICITA DI TITOLO, COMMERCIABILITÀ, IDONEITÀ PER UNO
              SCOPO PARTICOLARE, QUALITÀ SODDISFACENTE E NON VIOLAZIONE. OURA
              NON GARANTISCE L'ASSENZA DI ALCUNA INTERFERENZA CON LA FRUIZIONE
              DEI SERVIZI NÉ LA DISPONIBILITÀ DEI CONTENUTI E NON GARANTISCE CHE
              LE FUNZIONI CONTENUTE NEI SERVIZI SODDISFINO LE ESIGENZE, CHE I
              SERVIZI SIANO PRIVI DI VIRUS O ALTRI COMPONENTI DANNOSI, CHE IL
              FUNZIONAMENTO DEI SERVIZI SIA ININTERROTTO O PRIVO DI ERRORI, CHE
              I DIFETTI NEI SERVIZI SIANO CORRETTI O CHE LE FUNZIONI CONTENUTE
              NEI SERVIZI SIANO OPERATIVE CON ALTRE APP MOBILI O HARDWARE O
              ALL'INTERNO DI UN SISTEMA. NESSUNA INFORMAZIONE O SUGGERIMENTO, IN
              FORMA SIA SCRITTA SIA ORALE, PROVENIENTE DA OURA O DA UN
              RAPPRESENTANTE AUTORIZZATO DI OURA COSTITUIRÀ UNA GARANZIA. ALCUNE
              GIURISDIZIONI NON CONSENTONO L'ESCLUSIONE DI GARANZIE IMPLICITE O
              LIMITAZIONI SUI VIGENTI DIRITTI DEL CONSUMATORE PREVISTI DALLA
              LEGGE, PERTANTO L'ESCLUSIONE DI CUI SOPRA POTREBBE NON ESSERE
              APPLICABILE.{' '}
            </TypographyV3>
            <TypographyV3>
              FATTO SALVO QUANTO DETTO SOPRA, OURA GARANTISCE ALL'ACQUIRENTE
              ORIGINALE DELL'UTENTE FINALE CHE IL PRODOTTO È PRIVO DI DIFETTI DI
              MATERIALE E DI LAVORAZIONE PER UN (1) ANNO DALLA DATA DI ACQUISTO.
              Se la legge locale dell'Utente in vigore al momento dell'acquisto
              richiede un periodo di garanzia più lungo di un (1) anno, questa
              garanzia sarà estesa nella misura richiesta da tale legge. Durante
              il periodo di garanzia, Oura riparerà o sostituirà senza costi per
              l'Utente qualsiasi componente del Prodotto che non rispetti la
              garanzia limitata fornita. L'Utente sarà responsabile di tutte le
              relative spese di trasporto. I prodotti sostitutivi possono essere
              nuovi o rinnovati a nostra discrezione. Suddetta garanzia limitata
              non si applica a (i) normale usura, compresi graffi e ammaccature;
              (ii) parti consumabili incluse nel Prodotto, come le batterie, a
              meno che non siano occorsi danni al prodotto a causa di un difetto
              nei materiali o nella lavorazione; (iii) danni derivanti dal
              mancato utilizzo del Prodotto in conformità alle istruzioni a
              corredo del Prodotto Ōura o che sono disponibili sul nostro sito
              Web; (iv) danni derivanti da incidenti, inondazioni, incendi, uso
              improprio o abuso; (v) danni derivanti dall'assistenza fornita o
              da manomissioni o alterazioni del Prodotto, di cui in entrambi i
              casi i responsabili sono soggetti non autorizzati da Ōura; o (vi)
              uso del Prodotto con qualsiasi altra applicazione o software
              diverso dall'App Mobile.
            </TypographyV3>
            <TypographyV3>
              Oura si riserva il diritto esclusivo di riparare o sostituire il
              prodotto, o di offrire un rimborso completo, a sua esclusiva
              discrezione. Per qualsiasi violazione di questa garanzia limitata,
              tale rimedio sarà unico ed esclusivo. Le riparazioni e le
              sostituzioni in garanzia hanno una nuova garanzia che è l'opzione
              più lunga fra le due seguenti: novanta (90) giorni o il saldo
              della garanzia originale di un (1) anno.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Limitazione di responsabilità
            </TypographyV3>
            <TypographyV3>
              IN NESSUN CASO OURA, LE SUE CONTROLLATE E AFFILIATE E I SUOI
              FUNZIONARI, DIRETTORI, DIPENDENTI, RAPPRESENTANTI, AGENTI, PARTNER
              E LICENZIATARI SARANNO RESPONSABILI PER DANNI DI QUALSIASI TIPO,
              SECONDO QUALSIASI TEORIA LEGALE, DERIVANTI DA O IN CONNESSIONE CON
              L'USO O L'IMPOSSIBILITÀ DI USARE I SERVIZI O QUALSIASI CONTENUTO
              ASSOCIATO AI SERVIZI, O SITI SIMILI O QUALSIASI SERVIZIO O
              ELEMENTO OTTENUTO ATTRAVERSO I SERVIZI, COMPRESI EVENTUALI DANNI
              DIRETTI, INDIRETTI, SPECIALI, ACCIDENTALI, CONSEQUENZIALI O
              PUNITIVI, INCLUSI, A TITOLO ESEMPLIFICATIVO MA NON ESAUSTIVO,
              LESIONI PERSONALI, DOLORE E SOFFERENZA, STRESS EMOTIVO, PERDITA DI
              ENTRATE, PERDITA DI PROFITTI, PERDITA DI AFFARI O DI RISPARMI
              PREVISTI, PERDITA DI OPPORTUNITÀ COMMERCIALI, INTERRUZIONE
              DELL'ATTIVITÀ, PERDITA DI UTILIZZO, PERDITA DI AVVIAMENTO, PERDITA
              DI DATI E SE PROVOCATI DA ILLECITO (COMPRESA LA NEGLIGENZA),
              VIOLAZIONE DI CONTRATTO O ALTRO, ANCHE SE PREVEDIBILE. TALI
              ESCLUSIONI O LIMITAZIONI SI APPLICHERANNO INDIPENDENTEMENTE DAL
              FATTO CHE OURA SIA STATA AVVISATA O MENO DELLA POSSIBILITÀ DEI
              SUDDETTI DANNI.
            </TypographyV3>
            <TypographyV3>
              QUANTO SOPRA NON PREGIUDICA NESSUNA RESPONSABILITÀ CHE NON POSSA
              ESSERE ESCLUSA O LIMITATA AI SENSI DELLA LEGGE APPLICABILE. NELLA
              MISURA IN CUI LA RESPONSABILITÀ NON PUÒ ESSERE ESCLUSA O LIMITATA
              COME STABILITO SOPRA, IN NESSUN CASO OURA SARÀ RESPONSABILE PER
              QUALSIASI RICHIESTA DI RISARCIMENTO, SIA PER CONTRATTO, ILLECITO O
              IN BASE A QUALSIASI ALTRA TEORIA DI RESPONSABILITÀ, SUPERIORE A
              100 DOLLARI.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Precauzioni
            </TypographyV3>
            <TypographyV3>
              I SERVIZI NON SONO UN DISPOSITIVO MEDICO E L'UTENTE ACCETTA
              ESPRESSAMENTE CHE I SERVIZI NON COMPORTANO L'OFFERTA DI CONSIGLI
              MEDICI DA PARTE DI OURA. I SERVIZI NON SONO DESTINATI A
              DIAGNOSTICARE, TRATTARE, CURARE O PREVENIRE ALCUNA MALATTIA O
              CONDIZIONE MEDICA. I SERVIZI SONO SOLO A SCOPO INFORMATIVO E NON
              POSSONO SOSTITUIRE I SERVIZI DI MEDICI O DI PROFESSIONISTI DEL
              SETTORE MEDICO.
            </TypographyV3>
            <TypographyV3>
              I SERVIZI, COMPRESE TUTTE LE INFORMAZIONI, I TESTI, LE FOTOGRAFIE,
              LE IMMAGINI, LE ILLUSTRAZIONI, LA GRAFICA, I CLIP AUDIO, VIDEO E
              AUDIO-VIDEO E ALTRI MATERIALI, FORNITI DA NOI O DA TERZI, NON
              DEVONO ESSERE E NON DEVONO ESSERE UTILIZZATI IN SOSTITUZIONE DI
              (a) IL CONSIGLIO DEL MEDICO DI FIDUCIA O DI ALTRI PROFESSIONISTI
              MEDICI, (b) UNA VISITA, UNA CHIAMATA O UN CONSULTO CON IL MEDICO
              DI FIDUCIA O ALTRI PROFESSIONISTI MEDICI O (c) INFORMAZIONI
              CONTENUTE SU O IN UNA CONFEZIONE O ETICHETTA DEL PRODOTTO.
            </TypographyV3>
            <TypographyV3>
              IN CASO DI DOMANDE RELATIVE ALLA SALUTE, CHIAMARE O VEDERE
              PRONTAMENTE IL PROPRIO MEDICO O ALTRO FORNITORE DI SERVIZI MEDICI.
              IN CASO DI EMERGENZA, CHIAMARE IMMEDIATAMENTE IL MEDICO DI
              RIFERIMENTO O IL 118. L'UTENTE NON DEVE MAI IGNORARE UN CONSIGLIO
              MEDICO O RITARDARE LA RICERCA DI UN CONSIGLIO MEDICO A CAUSA DI
              QUALSIASI INFORMAZIONE PRESENTATA NEI SERVIZI, E NON DEVE
              UTILIZZARE I SERVIZI O QUALSIASI INFORMAZIONE FORNITA NEI SERVIZI
              PER DIAGNOSTICARE O TRATTARE UN PROBLEMA DI SALUTE. LA
              TRASMISSIONE E LA RICEZIONE DI SERVIZI, IN TUTTO O IN PARTE, O LA
              COMUNICAZIONE VIA INTERNET, E-MAIL O ALTRI MEZZI NON COSTITUISCE O
              CREA UN RAPPORTO MEDICO-PAZIENTE, TERAPEUTA-PAZIENTE O ALTRO
              RAPPORTO PROFESSIONALE SANITARIO TRA L'UTENTE E OURA.
            </TypographyV3>
            <TypographyV3>
              Consultare sempre un medico prima di apportare qualsivoglia
              modifica a sonno o attività sulla base delle informazioni fornite
              attraverso i Servizi, o se si hanno domande relativamente al
              proprio quadro medico. Oura non è responsabile per qualsiasi
              problema di salute che possa derivare da informazioni acquisite
              attraverso i Servizi. Se l'Utente apporta qualsiasi modifica a
              sonno o attività sulla base dei Servizi Oura, accetta di farlo a
              suo rischio e pericolo. È importante prestare attenzione alle
              risposte del proprio corpo. Ad esempio, se dopo aver modificato il
              sonno o le attività si avverte un'inaspettata, ripetuta o
              prolungata sensazione di dolore, stanchezza o disagio, consigliamo
              di rivolgersi a un medico prima di proseguire con tali modifiche.
              Le informazioni presenti nei Servizi possono essere fuorvianti, se
              le funzioni fisiologiche e le risposte differiscono
              significativamente dalla mediae della popolazione a causa di
              condizioni mediche o di rare differenze naturali.
            </TypographyV3>
            <TypographyV3>
              Prestare attenzione affinché il Prodotto indossato non rimanga
              impigliato in strutture fisse o oggetti pesanti. In caso di
              rossore o irritazioni cutanee al dito mentre si indossa il
              Prodotto, rimuoverlo immediatamente. Se anche senza indossare più
              il Prodotto, i sintomi persistono per più di 2-3 giorni,
              contattare un professionista.
            </TypographyV3>
            <TypographyV3>
              Il nostro Prodotto non deve mai essere messo in bocca. Il prodotto
              di Oura non è un giocattolo né è destinato all'uso da parte dei
              bambini. I bambini non devono essere lasciati incustoditi con
              questo Prodotto perché può costituire un pericolo di soffocamento.
            </TypographyV3>
            <TypographyV3>
              I Servizi possono contenere link ad altri siti web gestiti da
              terzi. L'Utente riconosce e accetta che tali link sono forniti
              solo per comodità e non riflettono alcuna approvazione,
              affiliazione, relazione o sponsorizzazione da parte di Oura
              rispetto al fornitore di tale sito collegato o alla qualità,
              affidabilità o qualsiasi altra caratteristica o funzionalità di
              tale sito collegato. Inoltre, l'Utente riconosce e accetta che
              Oura non è in alcun modo responsabile (incluso, senza limitazione,
              rispetto a qualsiasi perdita o lesione che si possa subire) per
              qualsiasi questione associata al sito collegato, incluso, senza
              limitazione, il contenuto fornito su o attraverso tale sito
              collegato o l'affidamento da parte dell'Utente ad esso. Inoltre,
              l'Utente deve essere consapevole che l'uso di qualsiasi sito di
              terze parti è soggetto ai termini e alle condizioni applicabili a
              tale sito, comprese le relative politiche sulla privacy (o la loro
              eventuale assenza). Se una terza parte si collega ai Servizi, non
              è necessariamente un'indicazione di approvazione, affiliazione,
              relazione o sponsorizzazione da o con Oura. Oura potrebbe anche
              non essere consapevole che una terza parte si è collegata ai
              Servizi.
            </TypographyV3>
            <TypographyV3>
              Qualsiasi altro contenuto non di proprietà di Oura è di proprietà
              del rispettivo proprietario. L'Utente riconosce e accetta che tali
              contenuti sono forniti dal loro proprietario e non riflettono
              alcuna approvazione, affiliazione, relazione o sponsorizzazione da
              parte di Oura rispetto al fornitore di tali contenuti. L'utente
              riconosce e accetta altresì che Oura non è responsabile in alcun
              modo (incluso, senza limitazioni, rispetto a qualsiasi perdita o
              danno che l'Utente possa subire) per qualsiasi contenuto fornito
              da terzi, incluso, senza limitazioni, il suo affidamento ad esso.
              OURA NON RILASCIA ALCUNA DICHIARAZIONE O GARANZIA RIGUARDO A
              QUALSIASI CONTENUTO DI TERZE PARTI.
            </TypographyV3>
            <TypographyV3>
              L'utente accetta di indennizzare, tenere indenne e difendere Oura,
              le sue consociate e affiliate, i suoi funzionari, direttori,
              dipendenti, rappresentanti, agenti, partner, licenziatari,
              successori e cessionari, da e contro qualsiasi azione, causa,
              reclamo, danno, debito, richiesta o responsabilità, compresi i
              costi ragionevoli e le spese legali, rivendicati da qualsiasi
              persona, derivanti da o relativi a (i) l'uso dei Servizi,
              compreso, ma non limitato, a chiunque utilizzi il suo account o le
              sue Credenziali; (ii) violazione del presente accordo da parte
              dell'utente o di chiunque utilizzi il suo account o le sue
              credenziali; (iii) qualsiasi informazione utilizzata, memorizzata
              o trasmessa in relazione al suo account o alle sue credenziali;
              (iv) violazione dei diritti di terzi, compresi, ma non limitati a
              privacy, pubblicità, proprietà intellettuale o altri diritti di
              proprietà da parte dell'utente o di chiunque utilizzi il suo
              account o le sue credenziali; o (v) violazione di qualsiasi legge,
              regolamento o altri requisiti legali.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Avviso per gli utenti della California
            </TypographyV3>
            <TypographyV3>
              Ai sensi della Sezione 1789.3 del Codice Civile della California,
              gli utenti californiani dei Servizi hanno diritto al seguente
              specifico avviso in materia di diritti dei consumatori:
            </TypographyV3>
            <TypographyV3 className="ml-8">
              La Complaint Assistance Unit della Division of Consumer Services
              del California Department of Consumer Affairs può essere
              contattata per iscritto al 1625 N. Market Blvd., Suite N 112,
              Sacramento, California 95834, o per telefono al numero (800)
              952-5210.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Scioglimento; Cancellazione
            </TypographyV3>
            <TypographyV3>
              Il presente Accordo continuerà ad avere pieno vigore fino a quando
              non sarà terminato o annullato ai sensi del presente Accordo.
            </TypographyV3>
            <TypographyV3>
              Oura ha il diritto di sciogliere il presente Accordo (i) dando
              all'Utente un preavviso di trenta (30) giorni per qualsiasi
              motivo; (ii) immediatamente per la violazione materiale del
              presente Accordo, che non sia il mancato pagamento delle
              Commissioni; o (iii) per il mancato pagamento delle Tariffe. Fatto
              quanto sopra, in qualsiasi momento e per qualsiasi motivo Oura si
              riserva il diritto, a sua sola discrezione e senza preavviso, di
              rimuovere, modificare, sospendere o disabilitare l'accesso a tutti
              o a qualsiasi parte dei Servizi.
            </TypographyV3>
            <TypographyV3>
              L'Utente può rescindere l'accordo per qualsiasi motivo fornendo un
              preavviso di trenta (30) giorni a Oura via e-mail all'indirizzo
              support@ouraring.com. L'Utente sarà responsabile per tutte le
              Tariffe sostenute prima e durante il periodo di preavviso.
            </TypographyV3>
            <TypographyV3>
              Le sezioni intitolate Precauzioni, Diritti di proprietà
              intellettuale, Dati e comunicazioni, Indennizzo, Esclusione di
              garanzia, Limitazione di responsabilità, Legge applicabile, Foro;
              Arbitrato vincolante obbligatorio; Rinuncia all'azione collettiva,
              e gli obblighi di pagamento per le Tariffe sostenute prima e
              durante qualsiasi periodo di preavviso sopravvivono allo
              scioglimento del presente Accordo, per qualsiasi motivo.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Restrizioni d'uso finale del governo federale (statunitense)
            </TypographyV3>
            <TypographyV3>
              Se l'Utente corrisponde a un dipartimento o un'agenzia del governo
              federale degli Stati Uniti o sta stipulando un contratto per conto
              di tale dipartimento o agenzia, i Servizi sono “Commercial Items”
              (articoli commerciali) come definito al 48 C.F.R. §2.101, e
              consistono in “Commercial Computer Software” (software commerciali
              per computer) e ”Commercial Computer Software Documentation”
              (documentazione di software commerciali per computer) e come
              indicato chiaramente nel 48 C.F.R. §12.212 o 48 C.F.R. §227.7202.
              In conformità al 48 C.F.R. §12.212 o 48 C.F.R. §227.7202-1 fino a
              227.7202-4, ove applicabile, il Servizio viene concesso in licenza
              all'Utente solo con i diritti previsti dai termini e dalle
              condizioni del presente Accordo. Se l'Utente corrisponde a
              un'attività ordinativa del governo federale degli Stati Uniti o è
              dipendente di tale attività ordinativa che effettua acquisti
              tramite un contratto GSA Schedule in qualità di agente per tale
              attività ordinativa, i termini del presente Accordo pubblicato e
              dell'Informativa sulla privacy potrebbero differire.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Conformità all'esportazione e restrizioni d'uso
            </TypographyV3>
            <TypographyV3>
              L'Utente non esporterà o riesporterà direttamente o indirettamente
              i Servizi, o qualsiasi informazione tecnica a essi relativa, verso
              qualsiasi destinazione o persona proibita o limitata dalla legge
              applicabile, incluse, senza limitazione, tutte le leggi e i
              regolamenti applicabili sul controllo delle esportazioni degli
              Stati Uniti
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Legge applicabile; Foro; Arbitrato vincolante obbligatorio,
              rinuncia all'azione collettiva
            </TypographyV3>
            <TypographyV3>
              Qualsiasi azione relativa al presente Accordo, i Servizi, e il
              rapporto con Oura saranno regolati, analizzati e interpretati in
              conformità alle leggi dello Stato della California, senza
              considerare il conflitto dei principi di legge E NON SARANNO
              REGOLATI DALLE CONVENZIONI DELLE NAZIONI UNITE SUI CONTRATTI PER
              LA VENDITA INTERNAZIONALE DI MERCI, SE DIVERSAMENTE APPLICABILI.
              L'Utente accetta di risolvere qualsiasi controversia o reclamo
              derivante da o collegato al presente Accordo o ai Servizi
              attraverso un arbitrato finale e vincolante da parte di un unico
              arbitro. Ciò include le controversie derivanti da o relative
              all'interpretazione o all'applicazione della presente sezione
              "Disposizione di arbitrato obbligatorio", compresa la sua
              applicabilità, revocabilità o validità. Fatto salvo quanto detto
              sopra, ciascuna parte può intentare una causa solo per ottenere un
              provvedimento ingiuntivo per fermare l'uso non autorizzato o
              l'abuso dei Servizi, o la violazione di qualsiasi proprietà
              intellettuale. Soggette alla disposizione sull'arbitrato
              obbligatorio, le parti acconsentono irrevocabilmente a portare
              qualsiasi azione al fine di risolvere o far valere i reclami
              derivanti da o relativi al presente Accordo davanti alle corti
              federali o statali di San Francisco, California, e ciascuna parte
              si sottopone irrevocabilmente alla giurisdizione esclusiva di tali
              corti in qualsiasi causa, azione o procedimento. Salvo nella
              misura in cui è proibito dalla legge applicabile, le parti
              concordano che qualsiasi reclamo o causa di azione derivante da o
              correlata all'uso dei Servizi o del presente Accordo deve essere
              presentato entro un (1) anno dal suddetto reclamo o dalla causa di
              azione o sarà per sempre precluso. Questo paragrafo non si applica
              agli utenti che risiedono nell'Unione Europea. Se l'Utente ha sede
              nell'Unione Europea, allora al presente Accordo si applicherà la
              legge finlandese e saranno i tribunali finlandesi ad avere la
              giurisdizione esclusiva per ascoltare le controversie derivanti
              dal presente Accordo. La presente disposizione non si applica ai
              consumatori di Paesi che richiedono che gli accordi siano regolati
              dalle leggi locali del Paese del consumatore. La lingua inglese
              regola tutti i documenti, gli avvisi e le interpretazioni del
              presente Accordo. L'Utente accetta inoltre di rinunciare a
              qualsiasi diritto di rivendicare qualsiasi reclamo contro Oura
              come rappresentante o membro in qualsiasi azione collettiva o
              rappresentativa, tranne quando tale rinuncia sia vietata dalla
              legge o ritenuta da un tribunale contro l'ordine pubblico.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Varie{' '}
            </TypographyV3>
            <TypographyV3>
              L'Utente riconosce che Oura ha il diritto di monitorare l'uso dei
              Servizi per assicurarne l'aderenza all'Accordo.
            </TypographyV3>
            <TypographyV3>
              Nessuna rinuncia a un eventuale termine, una disposizione o
              condizione del presente Accordo, sia per condotta o altrimenti, in
              uno o più casi, sarà considerata o costituirà una rinuncia a
              qualsiasi altro termine, disposizione o condizione del presente
              Accordo, che sia o meno simile, né tale rinuncia costituirà una
              rinuncia continua di qualsiasi termine, disposizione o condizione
              del presente Accordo. Nessuna rinuncia sarà vincolante a meno che
              non sia eseguita per iscritto dalla parte che fa la rinuncia.
            </TypographyV3>
            <TypographyV3>
              L'Utente non può cedere il presente Accordo a qualsiasi altra
              parte e qualsiasi tentativo di farlo è nullo.
            </TypographyV3>
            <TypographyV3>
              Se una qualsiasi disposizione del presente Accordo viene ritenuta
              illegale o inapplicabile, allora tale disposizione sarà applicata
              nella misura massima possibile, e le altre disposizioni rimarranno
              pienamente efficaci e applicabili.
            </TypographyV3>
            <TypographyV3>
              Il presente Accordo e la Politica sulla privacy costituiscono le
              dichiarazioni complete ed esclusive dell'accordo tra l'utente e
              Oura riguardo ai Servizi, e sostituiscono qualsiasi comunicazione,
              asserzione, dichiarazione e intesa precedente o contemporanea, sia
              orale sia scritta, tra le parti.
            </TypographyV3>
            <TypographyV3>
              In caso di eventuali conflitti tra i termini del presente Accordo
              e i termini della Politica sulla privacy, i termini del presente
              Accordo prevarranno.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Modifica dei Termini e dei Servizi
            </TypographyV3>
            <TypographyV3>
              Oura si riserva il diritto di aggiornare il presente Accordo e/o
              la Politica sulla privacy in qualsiasi momento e per qualsiasi
              motivo a sua esclusiva discrezione, pubblicando i termini
              aggiornati. Se non diversamente indicato da Oura, qualsiasi
              modifica entrerà in vigore a partire dalla data di pubblicazione.
              Oura informerà l'Utente di qualsiasi cambiamento materiale
              dell'Accordo o dei Servizi. Continuando ad accedere o a utilizzare
              i Servizi dopo che abbiamo fornito l'avviso di una modifica,
              l'Utente accetta di essere vincolato dall'Accordo modificato. Se
              l'Accordo modificato non è accettabile per l'Utente, l'unico
              ricorso è quello di cessare l'utilizzo dei Servizi. Oura e i suoi
              fornitori di servizi terzi possono apportare miglioramenti e/o
              modifiche a Servizi, funzionalità e prezzi descritti in qualsiasi
              momento e per qualsiasi motivo a loro esclusiva discrezione. L'App
              Mobile può scaricare e installare nuove versioni, aggiornamenti e
              funzionalità aggiuntive al fine di migliorare, potenziare e
              sviluppare ulteriormente i Servizi. Oura si riserva il diritto in
              qualsiasi momento di modificare o interrompere, temporaneamente o
              permanentemente, i Servizi o qualsiasi parte di essi con o senza
              preavviso. L'Utente accetta che Oura non sarà responsabile nei
              suoi confronti o nei confronti di terzi per qualsiasi modifica,
              sospensione o interruzione dei Servizi.
            </TypographyV3>
            {enable2024Terms ? (
              <TermsOfDeliverySection />
            ) : (
              <>
                <TypographyV3 Element="h1" variant="h1">
                  TERMINI DI CONSEGNA DI ŌURA
                </TypographyV3>
                <TypographyV3>
                  <em>Ultimo aggiornamento: 18 ottobre 2021</em>
                </TypographyV3>
                <TypographyV3>
                  Il Paese base del punto vendita di Oura è la Finlandia. Questo
                  può significare che il pagamento dell'Utente può essere
                  soggetto a una transazione estera qualora ricorresse a una
                  carta di credito. Si consiglia di reperire maggiori
                  informazioni presso l'istituto bancario che ha emesso la
                  carta. Gli anelli vengono spediti dall'UE. Oura consegna
                  secondo gli Incoterms® 2020 come discusso qui di seguito.
                </TypographyV3>
                <TypographyV3 Element="h2" variant="h3">
                  Unione europea e USA
                </TypographyV3>
                <TypographyV3>
                  DDP [Delivered Duty Paid] - Reso sdoganato
                </TypographyV3>
                <TypographyV3>
                  Oura è responsabile della consegna della merce al luogo
                  indicato nel Paese dell'acquirente, e paga tutti i costi di
                  consegna della merce a destinazione, compresi i dazi e le
                  tasse di importazione.
                </TypographyV3>
                <TypographyV3 Element="h2" variant="h3">
                  Al di fuori dell'Unione europea e degli USA
                </TypographyV3>
                <TypographyV3>
                  DAP [Delivered at Place] - Reso al luogo di destinazione
                </TypographyV3>
                <TypographyV3>
                  Oura paga il trasporto fino al luogo indicato, ad eccezione
                  dei costi relativi allo sdoganamento all'importazione, e si
                  assume tutti i rischi prima del punto in cui la merce è pronta
                  per lo scarico da parte dell'acquirente.
                </TypographyV3>
              </>
            )}
          </>
          <TypographyV3 className="mt-40">
            {t('legal_disclaimer_translations')}
          </TypographyV3>
        </TypographyRhythm>
      </LayoutGrid>
    </PageLayoutV3>
  );
};

Page.pageName = 'Terms and Conditions';
Page.isSormusCompatible = true;

export default Page;
